<template>
  <section v-if="groups.length" class="builder-filters" aria-label="Part filters">
    <div class="builder-filter-bar">
      <span class="quick-filter-label">Filters</span>
      <div ref="quickFilterChips" class="quick-filter-chips">
        <button
          v-for="option in visibleQuickOptions"
          :key="option.groupKey + ':' + option.value"
          type="button"
          class="filter-chip"
          :class="{ active: isActive(option.groupKey, option.value) }"
          :aria-pressed="isActive(option.groupKey, option.value) ? 'true' : 'false'"
          :title="'Filter by ' + option.groupLabel + ': ' + option.label"
          @click="$emit('toggle', option.groupKey, option.value)"
        >
          <span>{{ option.label }}</span>
          <small>{{ option.count }}</small>
        </button>
        <div class="quick-filter-measure" aria-hidden="true">
          <span
            v-for="option in quickOptions"
            :key="'measure:' + option.groupKey + ':' + option.value"
            ref="quickFilterMeasureChip"
            class="filter-chip"
          >
            <span>{{ option.label }}</span>
            <small>{{ option.count }}</small>
          </span>
        </div>
      </div>
      <div class="filter-actions">
        <button
        type="button"
        class="all-filters-button"
        :class="{ active: expanded || activeCount }"
        :aria-expanded="expanded ? 'true' : 'false'"
        @click="expanded = !expanded"
      >
        All filters
        <span v-if="activeCount">{{ activeCount }}</span>
        <i aria-hidden="true">{{ expanded ? '−' : '+' }}</i>
        </button>
        <button
          v-if="activeCount"
          type="button"
          class="clear-filters-button"
          @click="$emit('clear')"
        >Clear</button>
      </div>
    </div>

    <transition name="filter-panel">
      <div v-if="expanded" class="all-filters-panel">
        <div class="all-filters-head">
          <div>
            <span>Filter {{ typeLabel }}</span>
            <strong>Available specifications</strong>
          </div>
          <small>{{ resultCount }} of {{ totalCount }} shown</small>
        </div>
        <div class="filter-groups">
          <section v-for="group in groups" :key="group.key" class="filter-group">
            <h6>{{ group.label }}</h6>
            <div>
              <button
                v-for="option in group.options"
                :key="group.key + ':' + option.value"
                type="button"
                class="filter-value"
                :class="{ active: isActive(group.key, option.value) }"
                :aria-pressed="isActive(group.key, option.value) ? 'true' : 'false'"
                @click="$emit('toggle', group.key, option.value)"
              >
                <span>{{ option.label }}</span>
                <small>{{ option.count }}</small>
              </button>
            </div>
          </section>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
export default {
  name: 'BuilderFilters',
  props: {
    groups: {
      type: Array,
      default: () => []
    },
    selected: {
      type: Object,
      default: () => ({})
    },
    resultCount: {
      type: Number,
      default: 0
    },
    totalCount: {
      type: Number,
      default: 0
    },
    typeLabel: {
      type: String,
      default: 'parts'
    }
  },
  data () {
    return {
      expanded: false,
      visibleQuickCount: null,
      quickFilterResizeObserver: null
    }
  },
  computed: {
    quickOptions () {
      const groups = this.groups.filter((group) => group.options && group.options.length)
      const leadingOptions = []
      const remainingOptions = []
      const seen = {}
      const quickOption = (group, option) => {
        return Object.assign({}, option, {
          groupKey: group.key,
          groupLabel: group.label
        })
      }
      const optionKey = (option) => {
        return option.groupKey + ':' + String(option.value).toLowerCase()
      }

      groups.forEach((group) => {
        const preferred = (group.preferredValues || [])
          .map((value) => String(value).toLowerCase())
        const option = group.options.find((entry) => {
          return preferred.indexOf(String(entry.value).toLowerCase()) >= 0
        }) || group.options[0]
        const candidate = quickOption(group, option)
        seen[optionKey(candidate)] = true
        leadingOptions.push(candidate)
      })

      groups.forEach((group) => {
        group.options.forEach((option) => {
          const candidate = quickOption(group, option)
          const key = optionKey(candidate)
          if (!seen[key]) {
            seen[key] = true
            remainingOptions.push(candidate)
          }
        })
      })

      remainingOptions.sort((left, right) => {
        return Number(right.count || 0) - Number(left.count || 0) ||
          String(left.label).localeCompare(String(right.label), undefined, { numeric: true })
      })

      return leadingOptions.concat(remainingOptions)
    },
    visibleQuickOptions () {
      if (this.visibleQuickCount === null) {
        return this.quickOptions
      }
      return this.quickOptions.slice(0, this.visibleQuickCount)
    },
    activeCount () {
      return Object.keys(this.selected || {}).reduce((total, key) => {
        const values = this.selected[key]
        return total + (Array.isArray(values) ? values.length : 0)
      }, 0)
    }
  },
  watch: {
    groups () {
      this.expanded = false
      this.visibleQuickCount = null
      this.$nextTick(this.updateVisibleQuickOptions)
    },
    // The panel is absolutely positioned inside the parent's stacking context,
    // so the parent is the only place that can lift it over the sort toolbar.
    expanded (value) {
      this.$emit('expand-change', value)
    }
  },
  mounted () {
    this.$nextTick(this.updateVisibleQuickOptions)
    if (typeof window !== 'undefined' && window.ResizeObserver) {
      this.quickFilterResizeObserver = new window.ResizeObserver(this.updateVisibleQuickOptions)
      if (this.$refs.quickFilterChips) {
        this.quickFilterResizeObserver.observe(this.$refs.quickFilterChips)
      }
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateVisibleQuickOptions)
    }
  },
  beforeDestroy () {
    if (this.quickFilterResizeObserver) {
      this.quickFilterResizeObserver.disconnect()
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateVisibleQuickOptions)
    }
  },
  methods: {
    updateVisibleQuickOptions () {
      const container = this.$refs.quickFilterChips
      let chips = this.$refs.quickFilterMeasureChip || []
      if (!container) {
        return
      }
      // Phones use a touch-scrollable strip so useful quick filters are not
      // silently removed. The All filters action remains pinned beside it.
      if (typeof window !== 'undefined' && window.innerWidth <= 560) {
        if (this.visibleQuickCount !== null) {
          this.visibleQuickCount = null
        }
        return
      }
      if (!Array.isArray(chips)) {
        chips = [chips]
      }

      const availableWidth = container.clientWidth
      const gap = 6
      let usedWidth = 0
      let visibleCount = 0

      for (let index = 0; index < chips.length; index += 1) {
        const chip = chips[index]
        const requiredWidth = chip.offsetWidth + (visibleCount > 0 ? gap : 0)
        if (usedWidth + requiredWidth > availableWidth) {
          break
        }
        usedWidth += requiredWidth
        visibleCount += 1
      }
      if (this.visibleQuickCount !== visibleCount) {
        this.visibleQuickCount = visibleCount
      }
    },
    isActive (group, value) {
      const values = this.selected && this.selected[group]
      return Array.isArray(values) && values.indexOf(value) >= 0
    }
  }
}
</script>

<style scoped>
.builder-filters {
  display: contents;
}

.builder-filter-bar {
  display: flex;
  grid-column: 2;
  grid-row: 1;
  align-items: center;
  align-self: start;
  gap: 7px;
  min-width: 0;
  margin-top: 3px;
}

.quick-filter-label {
  color: #6f788b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: nowrap;
}

.quick-filter-chips {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
}

.quick-filter-measure {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  gap: 6px;
  width: max-content;
  visibility: hidden;
  pointer-events: none;
}

.filter-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}

.filter-chip,
.all-filters-button,
.clear-filters-button,
.filter-value {
  appearance: none;
  border: 1px solid rgba(67, 72, 87, 0.76);
  outline: none;
  background: rgba(12, 16, 24, 0.66);
  box-shadow: none;
  color: #a9b2c3;
  cursor: pointer;
  font-family: inherit;
  font-weight: 700;
  transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease, color 150ms ease;
}

.filter-chip {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
  min-height: 21px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 1;
  white-space: nowrap;
}

.filter-chip small,
.filter-value small {
  color: #6f788b;
  font-size: 9px;
  font-weight: 800;
}

.filter-chip:hover,
.filter-value:hover,
.all-filters-button:hover {
  border-color: rgba(32, 184, 235, 0.58) !important;
  background: rgba(25, 151, 198, 0.08);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.05);
  color: #d5f2fb;
}

.filter-chip.active,
.filter-value.active,
.all-filters-button.active {
  border-color: rgba(32, 184, 235, 0.88) !important;
  background: linear-gradient(180deg, rgba(25, 151, 198, 0.18), rgba(25, 151, 198, 0.09));
  box-shadow:
    inset 0 0 0 1px rgba(32, 184, 235, 0.09),
    0 0 12px rgba(25, 151, 198, 0.08);
  color: #d5f2fb;
}

.filter-chip:focus,
.filter-value:focus,
.all-filters-button:focus,
.clear-filters-button:focus {
  outline: none;
}

.filter-chip:focus-visible,
.filter-value:focus-visible,
.all-filters-button:focus-visible {
  border-color: #20b8eb !important;
  outline: none;
  box-shadow: 0 0 0 2px rgba(32, 184, 235, 0.18);
}

.clear-filters-button:focus-visible {
  border-color: transparent !important;
  outline: none;
  box-shadow: 0 0 0 2px rgba(32, 184, 235, 0.12);
  color: #cfeef8;
}

.filter-chip.active small,
.filter-value.active small {
  color: #69cceb;
}

.all-filters-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 27px;
  margin-left: 0;
  padding: 0 9px;
  border-radius: 6px;
  font-size: 11px;
  white-space: nowrap;
}

.all-filters-button > span {
  display: inline-grid;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  place-items: center;
  border-radius: 999px;
  background: #1997c6;
  color: #061019;
  font-size: 9px;
  font-weight: 900;
}

.all-filters-button i {
  color: #6fcdec;
  font-size: 14px;
  font-style: normal;
  line-height: 1;
}

.clear-filters-button {
  min-height: 27px;
  padding: 0 7px;
  border-color: transparent;
  background: transparent;
  color: #7f899b;
  font-size: 10px;
}

.clear-filters-button:hover {
  color: #cfeef8;
}

.all-filters-panel {
  position: absolute;
  z-index: 50;
  top: calc(100% + 8px);
  right: 0;
  left: 0;
  min-width: 0;
  margin-top: 0;
  padding: 12px;
  border: 1px solid rgba(67, 72, 87, 0.74);
  border-radius: 8px;
  /* Fully opaque: at 0.97 opacity over a 0.98 background, catalog card text and
     prices stayed legible through the panel and read as a rendering glitch. */
  background:
    radial-gradient(circle at 6% 0%, rgba(25, 151, 198, 0.08), transparent 32%),
    #080b11;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.5);
}

.all-filters-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.all-filters-head > div {
  display: grid;
  gap: 1px;
}

.all-filters-head span {
  color: #1997c6;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.all-filters-head strong {
  color: #dce2ed;
  font-size: 12px;
}

.all-filters-head > small {
  color: #7f899b;
  font-size: 10px;
  font-weight: 700;
}

.filter-groups {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.filter-group {
  min-width: 0;
}

.filter-group h6 {
  margin: 0 0 6px;
  color: #8f98aa;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.filter-group > div {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.filter-value {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 25px;
  padding: 0 7px;
  border-radius: 5px;
  font-size: 10px;
}

.filter-panel-enter-active,
.filter-panel-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}

.filter-panel-enter,
.filter-panel-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 980px) {
  .builder-filter-bar {
    grid-column: 1;
    grid-row: 2;
    margin-top: 0;
  }

}

@media (max-width: 900px) {
  .builder-filter-bar {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .quick-filter-chips {
    flex: 1 1 320px;
    max-width: calc(100vw - 160px);
    overflow: hidden;
    padding-bottom: 2px;
  }

  .filter-groups {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .builder-filter-bar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 6px 8px;
    width: 100%;
  }

  .quick-filter-label {
    grid-column: 1 / -1;
    width: auto;
  }

  .quick-filter-chips {
    grid-column: 1;
    grid-row: 2;
    max-width: none;
    padding: 0 2px 3px 0;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-inline: contain;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .quick-filter-chips::-webkit-scrollbar {
    display: none;
  }

  .filter-actions {
    grid-column: 2;
    grid-row: 2;
    align-self: center;
  }

  .filter-groups {
    grid-template-columns: 1fr;
  }
}
</style>
